import { useEffect, useRef, useState } from "react";
import { Award, Copy, Download, Eye, Lock, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtMYT } from "@/lib/myt";

export default function MemberCertificates() {
  const [certs, setCerts] = useState(null);
  const [templates, setTemplates] = useState({}); // code -> blob URL (template preview)
  const [viewing, setViewing] = useState(null); // {code, url, cert}
  const [busy, setBusy] = useState("");
  const blobUrls = useRef([]);

  const track = (url) => {
    blobUrls.current.push(url);
    return url;
  };

  const load = async () => {
    try {
      const res = await api.get("/member/certificates");
      setCerts(res.data.certificates);
      // template previews (blurred while locked, crisp when unlocked)
      for (const cert of res.data.certificates) {
        api.get(`/certificates/template/${cert.code}`, { responseType: "blob" })
          .then((r) => setTemplates((t) => ({ ...t, [cert.code]: track(URL.createObjectURL(r.data)) })))
          .catch(() => {});
      }
      const justUnlocked = res.data.certificates.filter((c) => c.just_unlocked);
      if (justUnlocked.length > 0) {
        toast.success(`Certificate unlocked — ${justUnlocked.map((c) => c.rank_name).join(", ")}!`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setCerts([]);
    }
  };

  useEffect(() => {
    load();
    const held = blobUrls.current;
    return () => held.forEach((u) => URL.revokeObjectURL(u));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewCertificate = async (cert) => {
    setBusy(`view-${cert.code}`);
    try {
      const res = await api.get(`/member/certificates/${cert.code}/image`, { responseType: "blob" });
      setViewing({ cert, url: track(URL.createObjectURL(res.data)) });
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy("");
  };

  const downloadCertificate = async (cert) => {
    setBusy(`dl-${cert.code}`);
    try {
      const res = await api.get(`/member/certificates/${cert.code}/download`, { responseType: "blob" });
      const url = track(URL.createObjectURL(res.data));
      const a = document.createElement("a");
      a.href = url;
      a.download = `PlusMaker-Certificate-${cert.certificate_id}.png`;
      a.click();
      toast.success("Certificate downloaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy("");
  };

  const copyText = (text) => {
    if (!navigator.clipboard?.writeText) {
      toast.error("Copy failed — please copy the text manually.");
      return;
    }
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success("Copied to clipboard"))
      .catch(() => toast.error("Copy failed — please copy the text manually."));
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="member-certificates-page">
      <style>{`
        @keyframes certGlow {
          0% { box-shadow: 0 0 0 rgba(212,175,55,0); opacity: .6; }
          40% { box-shadow: 0 0 46px rgba(212,175,55,.38); opacity: 1; }
          100% { box-shadow: 0 0 0 rgba(212,175,55,0); opacity: 1; }
        }
        .cert-just-unlocked { animation: certGlow 2.6s ease-out 2; border-color: rgba(212,175,55,.55) !important; }
      `}</style>

      <div>
        <p className="eyebrow-label">Achievements</p>
        <h1 className="page-title mt-2">My Certificates</h1>
        <p className="page-sub mt-1">
          Five milestones on your PlusMaker journey. Certificates unlock automatically the moment your rank reaches them — and once earned, they are yours forever.
        </p>
      </div>

      {certs === null ? (
        <p className="text-[#64748B] text-sm" data-testid="certificates-loading">Loading your certificate collection…</p>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {certs.map((cert) => (
            <div
              key={cert.code}
              className={`card-lux overflow-hidden ${cert.just_unlocked ? "cert-just-unlocked" : ""}`}
              data-testid={`cert-card-${cert.code}`}
            >
              <div className="relative aspect-[3/2] bg-[#0B0C10]">
                {templates[cert.code] ? (
                  <img
                    src={templates[cert.code]}
                    alt={`${cert.rank_name} certificate`}
                    className={`w-full h-full object-cover transition-all duration-500 ${cert.unlocked ? "" : "blur-[6px] brightness-[.38] scale-[1.02]"}`}
                    data-testid={`cert-preview-${cert.code}`}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Award className="h-8 w-8 text-[#D4AF37]/30" />
                  </div>
                )}
                {!cert.unlocked && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                    <Lock className="h-6 w-6 text-[#D4AF37]" />
                    <p className="text-[10px] font-mono tracking-widest uppercase text-[#94A3B8]" data-testid={`cert-requires-${cert.code}`}>
                      Requires {cert.rank_name}
                    </p>
                  </div>
                )}
                <span
                  className={`absolute top-3 right-3 text-[9px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${
                    cert.unlocked
                      ? "text-black bg-[#D4AF37] border-[#D4AF37]"
                      : "text-[#94A3B8] border-[#222530] bg-[#050506]/80"
                  }`}
                  data-testid={`cert-status-${cert.code}`}
                >
                  {cert.unlocked ? "Unlocked" : "Locked"}
                </span>
              </div>

              <div className="p-5 space-y-4">
                <div>
                  <p className="text-sm font-semibold text-white">{cert.rank_name}</p>
                  <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] mt-0.5">{cert.concept}</p>
                  {cert.unlocked && (
                    <p className="text-[11px] text-[#94A3B8] font-mono mt-1.5" data-testid={`cert-meta-${cert.code}`}>
                      {cert.certificate_id} · Achieved {fmtMYT(cert.achieved_at, false)}
                    </p>
                  )}
                </div>

                {cert.unlocked ? (
                  <div className="flex gap-2">
                    <button onClick={() => viewCertificate(cert)} disabled={busy === `view-${cert.code}`} className="btn-gold flex-1 py-2.5 text-xs flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid={`cert-view-${cert.code}`}>
                      <Eye className="h-3.5 w-3.5" /> {busy === `view-${cert.code}` ? "Rendering…" : "View Certificate"}
                    </button>
                    <button onClick={() => downloadCertificate(cert)} disabled={busy === `dl-${cert.code}`} className="btn-gold-outline flex-1 py-2.5 text-xs flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid={`cert-download-${cert.code}`}>
                      <Download className="h-3.5 w-3.5" /> {busy === `dl-${cert.code}` ? "Preparing…" : "Download"}
                    </button>
                  </div>
                ) : (
                  <p className="text-[11px] text-[#64748B] leading-relaxed">Reach the {cert.rank_name} rank to unlock this certificate.</p>
                )}

                {cert.unlocked && cert.social?.campaign_enabled && (
                  <div className="border-t border-[#222530] pt-4 space-y-2.5" data-testid={`cert-share-${cert.code}`}>
                    <p className="text-[10px] font-mono tracking-widest uppercase text-[#D4AF37]">Share Your Achievement</p>
                    {cert.social.caption && (
                      <div className="flex items-start gap-2">
                        <p className="flex-1 text-[11px] text-[#94A3B8] leading-relaxed italic">“{cert.social.caption}”</p>
                        <button onClick={() => copyText(`${cert.social.caption} ${cert.social.hashtags || ""}`.trim())} className="text-[#64748B] hover:text-[#D4AF37] transition-colors shrink-0" data-testid={`cert-copy-${cert.code}`} aria-label="Copy caption">
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                    {cert.social.hashtags && <p className="text-[10px] font-mono text-[#D4AF37]/80">{cert.social.hashtags}</p>}
                    {cert.social.instructions && <p className="text-[11px] text-[#64748B] leading-relaxed">{cert.social.instructions}</p>}
                    {cert.social.contest && <p className="text-[11px] text-[#94A3B8] leading-relaxed border border-[#D4AF37]/20 bg-[#D4AF37]/5 rounded-lg px-3 py-2">{cert.social.contest}</p>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={() => setViewing(null)} />
          <div className="relative max-w-5xl w-full space-y-3" data-testid="cert-view-modal">
            <div className="flex items-center justify-between">
              <p className="text-xs font-mono text-[#94A3B8] tracking-widest uppercase">
                {viewing.cert.certificate_id} · {viewing.cert.member_name}
              </p>
              <button onClick={() => setViewing(null)} className="text-[#94A3B8] hover:text-white p-1" data-testid="cert-view-close" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <img src={viewing.url} alt={`${viewing.cert.rank_name} certificate`} className="w-full rounded-xl border border-[#D4AF37]/30 shadow-2xl shadow-[#D4AF37]/10" />
          </div>
        </div>
      )}
    </div>
  );
}
